import {
	faFile,
	faFilePdf,
	faPlus,
	faSpinner,
	faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { ApiService, formatFileSize } from '@supply-mover-2-0/core';
import type {
	AutomationEmailAttachmentModel,
	AutomationEmailEntityDocumentType,
} from '@supply-mover-2-0/shared';
import {
	AUTOMATION_EMAIL_ENTITY_DOCUMENT_TYPES,
	getMaxTotalAttachmentBytes,
} from '@supply-mover-2-0/shared';
import { useMemo, useRef, useState } from 'react';
import { Icon } from '../../components/base/Icon';
import { Button } from '../../components/forms/Button';
import { Select } from '../../components/forms/Select';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Toast } from '../../services/ToastService';
import type { AvailableDataSource } from './DynamicFieldValue';
import { DynamicFieldValue } from './DynamicFieldValue';

interface EmailAttachmentsFieldProps {
	attachments?: Array<AutomationEmailAttachmentModel>;
	onChange: (attachments: Array<AutomationEmailAttachmentModel>) => void;
	getAvailableDataSources: () => Array<AvailableDataSource>;
	getDisplayText: (value: string) => string;
}

interface FileUploadResponse {
	fileId: number;
	uploadUrl: string;
}

// Tenant automation sends do not go through a connected mailbox, so the
// strictest (default) provider budget applies.
const MAX_TOTAL_BYTES = getMaxTotalAttachmentBytes(undefined);

const DOCUMENT_TYPE_LABELS: Record<AutomationEmailEntityDocumentType, string> =
	{
		quote_pdf: 'Quote PDF',
		order_pdf: 'Order PDF',
		invoice_pdf: 'Invoice PDF',
	};

const getDocumentTypeLabel = (
	type: AutomationEmailEntityDocumentType,
): string => DOCUMENT_TYPE_LABELS[type];

export function EmailAttachmentsField({
	attachments,
	onChange,
	getAvailableDataSources,
	getDisplayText,
}: EmailAttachmentsFieldProps): React.JSX.Element {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [isUploading, setIsUploading] = useState(false);
	const [showDocumentForm, setShowDocumentForm] = useState(false);
	const [documentType, setDocumentType] = useState<
		AutomationEmailEntityDocumentType | ''
	>('');
	const [documentEntityId, setDocumentEntityId] = useState('');

	const items = useMemo(() => attachments ?? [], [attachments]);

	// Only `file` entries have a size we can measure here — generated documents
	// are rendered at send time.
	const usedBytes = useMemo(
		() =>
			items.reduce(
				(sum, attachment) =>
					attachment.kind === 'file' ? sum + attachment.size : sum,
				0,
			),
		[items],
	);

	const removeAttachment = (index: number): void => {
		onChange(items.filter((_, i) => i !== index));
	};

	const uploadFile = async (
		file: File,
	): Promise<AutomationEmailAttachmentModel | null> => {
		try {
			const response = await ApiService.apiPost<FileUploadResponse>(
				'/files/upload',
				{
					name: file.name,
					contentType: file.type,
					mimetype: file.type,
					isPublic: false,
					type: 'email',
					subtype: 'automation-attachment',
				},
			);

			await fetch(response.uploadUrl, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			return {
				kind: 'file',
				fileId: response.fileId,
				name: file.name,
				size: file.size,
			};
		} catch (error) {
			console.error('Failed to upload automation attachment:', error);
			return null;
		}
	};

	const handleFilesSelected = async (
		event: React.ChangeEvent<HTMLInputElement>,
	): Promise<void> => {
		const selected = Array.from(event.target.files ?? []);
		// Allow re-selecting the same file after a removal.
		event.target.value = '';

		if (selected.length === 0) {
			return;
		}

		setIsUploading(true);
		try {
			let runningBytes = usedBytes;
			const added: Array<AutomationEmailAttachmentModel> = [];

			for (const file of selected) {
				if (runningBytes + file.size > MAX_TOTAL_BYTES) {
					Toast.show({
						type: 'error',
						text1: t('Attachment too large'),
						text2: `${t(file.name)} ${t('would exceed the')} ${formatFileSize(
							MAX_TOTAL_BYTES,
						)} ${t('total attachment limit')}`,
					});
					continue;
				}

				const uploaded = await uploadFile(file);
				if (uploaded == null) {
					Toast.show({
						type: 'error',
						text1: t('Failed to upload file'),
						text2: t(file.name),
					});
					continue;
				}

				runningBytes += file.size;
				added.push(uploaded);
			}

			if (added.length > 0) {
				onChange([...items, ...added]);
			}
		} finally {
			setIsUploading(false);
		}
	};

	const handleAddDocument = (): void => {
		if (documentType === '' || documentEntityId.trim() === '') {
			return;
		}

		onChange([
			...items,
			{
				kind: 'entity_document',
				documentType,
				entityId: documentEntityId.trim(),
			},
		]);

		setDocumentType('');
		setDocumentEntityId('');
		setShowDocumentForm(false);
	};

	const documentTypeOptions = AUTOMATION_EMAIL_ENTITY_DOCUMENT_TYPES.map(
		(type: AutomationEmailEntityDocumentType) => ({
			value: type,
			label: t(getDocumentTypeLabel(type)),
		}),
	);

	return (
		<div>
			<div className="flex items-center justify-between mb-3">
				<label className={`text-sm font-medium ${colors.classes.textPrimary}`}>
					{t('Attachments')}
				</label>
				<div className="flex items-center gap-2">
					<Button
						size="xs"
						variant="outline"
						disabled={isUploading}
						onClick={() => {
							fileInputRef.current?.click();
						}}
					>
						<Icon
							icon={isUploading ? faSpinner : faPlus}
							className={isUploading ? 'animate-spin' : undefined}
							size={12}
						/>
						<span className="ml-1">
							{isUploading ? t('Uploading...') : t('Add File')}
						</span>
					</Button>
					<Button
						size="xs"
						variant="outline"
						onClick={() => {
							setShowDocumentForm(!showDocumentForm);
						}}
					>
						<Icon icon={faFilePdf} size={12} />
						<span className="ml-1">{t('Add Document')}</span>
					</Button>
				</div>
			</div>

			<input
				ref={fileInputRef}
				type="file"
				multiple
				className="hidden"
				onChange={(event) => {
					void handleFilesSelected(event);
				}}
			/>

			{items.length > 0 && (
				<ul className="space-y-2 mb-3">
					{items.map((attachment, index) => (
						<li
							key={
								attachment.kind === 'file'
									? `file-${attachment.fileId}-${index}`
									: `doc-${attachment.documentType}-${index}`
							}
							className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${colors.classes.borderDefault} ${colors.classes.bgSecondary}`}
						>
							<div className="flex items-center gap-2 min-w-0">
								<Icon
									icon={attachment.kind === 'file' ? faFile : faFilePdf}
									size={14}
									className={colors.classes.textSecondary}
								/>
								<div className="min-w-0">
									<div
										className={`text-sm truncate ${colors.classes.textPrimary}`}
									>
										{attachment.kind === 'file'
											? t(attachment.name)
											: t(getDocumentTypeLabel(attachment.documentType))}
									</div>
									<div className={`text-xs ${colors.classes.textSecondary}`}>
										{attachment.kind === 'file'
											? formatFileSize(attachment.size)
											: t(getDisplayText(attachment.entityId))}
									</div>
								</div>
							</div>
							<Button
								size="xs"
								variant="ghost"
								onClick={() => {
									removeAttachment(index);
								}}
								aria-label={t('Remove attachment')}
							>
								<Icon icon={faTrash} size={12} variant="danger" />
							</Button>
						</li>
					))}
				</ul>
			)}

			{showDocumentForm && (
				<div
					className={`rounded-md border p-3 mb-3 space-y-3 ${colors.classes.borderDefault}`}
				>
					<div>
						<label
							className={`block text-sm font-medium mb-1 ${colors.classes.textPrimary}`}
						>
							{t('Document')}
						</label>
						<Select
							options={documentTypeOptions}
							value={documentType}
							placeholder={t('Select a document type')}
							onChange={(value) => {
								setDocumentType(value as AutomationEmailEntityDocumentType);
							}}
						/>
					</div>
					<DynamicFieldValue
						label={t('Entity ID')}
						value={documentEntityId}
						onChange={setDocumentEntityId}
						dataType="number"
						placeholder={t('Select the record the document is generated for')}
						availableData={getAvailableDataSources()}
						displayTransform={getDisplayText}
					/>
					<div className="flex items-center gap-2">
						<Button
							size="sm"
							variant="primary"
							disabled={documentType === '' || documentEntityId.trim() === ''}
							onClick={handleAddDocument}
						>
							{t('Add')}
						</Button>
						<Button
							size="sm"
							variant="ghost"
							onClick={() => {
								setShowDocumentForm(false);
							}}
						>
							{t('Cancel')}
						</Button>
					</div>
				</div>
			)}

			<p className={`text-sm ${colors.classes.textSecondary}`}>
				{t(
					'Files are uploaded once and sent with every email. Generated documents are rendered at send time for the record you point them at.',
				)}
			</p>
			<p className={`text-sm mt-1 ${colors.classes.textSecondary}`}>
				{`${formatFileSize(usedBytes)} ${t('of')} ${formatFileSize(
					MAX_TOTAL_BYTES,
				)} ${t('used')}. ${t(
					'Generated documents are not counted toward the limit here.',
				)}`}
			</p>
		</div>
	);
}
